import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import {
  Bell,
  Bot,
  CalendarClock,
  Clock,
  Mail,
  MessageSquare,
  MessageSquareText,
  PhoneCall,
  Plus,
} from 'lucide-react';
import { z } from 'zod';
import { Button } from '@/components/atoms/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/atoms/dropdown-menu';
import { NumberInput } from '@/components/molecules/NumberInput';
import { Label } from '@/components/atoms/label';
import { Switch } from '@/components/atoms/switch';
import { CollapsibleSectionHeader, FormAccordionCard, HintTooltip } from '@/components/molecules';
import type { FormPublicState } from '@/components/organisms/settings-reminders/ReminderPolicyEditSheet';
import { AiTogglesPanel } from '@/components/organisms/settings-reminders/AiTogglesPanel';
import { CadenceEditor } from '@/components/organisms/settings-reminders/CadenceEditor';
import {
  formStepToCadence,
  makeCadenceStepForChannel,
  offsetMinutesToFormValue,
  type CadenceFormStep,
} from '@/components/organisms/settings-reminders/cadence-utils';
import { QuietHoursEditor } from '@/components/organisms/settings-reminders/QuietHoursEditor';
import { TemplateEditor } from '@/components/organisms/settings-reminders/TemplateEditor';
import { useUpdateReminderPolicy } from '@/hooks/reminder-policies/useUpdateReminderPolicy';
import type {
  QuietHoursConfig,
  ReminderChannel,
  ReminderPolicy,
  TemplateConfig,
  UpdateReminderPolicyDto,
} from '@/types/reminder-policy';

interface ReminderPolicyFormProps {
  policy: ReminderPolicy;
  formId: string;
  readOnly?: boolean;
  onSaved?: () => void;
  onStateChange?: (s: FormPublicState) => void;
}

interface FormState {
  enabled: boolean;
  cadence: CadenceFormStep[];
  maxAttempts: number;
  quietHours: QuietHoursConfig | null;
  aiPersonalization: boolean;
  aiSendTimeOptimization: boolean;
  aiChannelSelection: boolean;
  aiVoiceFallback: boolean;
  templates: TemplateConfig[];
}

interface FormErrors {
  cadenceRoot?: string;
  cadence: Array<string | undefined>;
  maxAttempts?: string;
  quietHours?: string;
}

type SectionKey = 'delivery' | 'cadence' | 'quietHours' | 'ai' | 'templates';

const cadenceStepSchema = z.object({
  offsetValue: z.number().int('Offset must be a whole number'),
  offsetUnit: z.enum(['m', 'h', 'd']),
  channel: z.enum(['SMS', 'EMAIL', 'VOICE']),
  templateKey: z.string().optional(),
});

const quietHoursSchema = z
  .object({
    startMinute: z.number().int().min(0).max(1439),
    endMinute: z.number().int().min(0).max(1439),
    tz: z.string().min(1, 'Timezone is required'),
  })
  .refine((qh) => qh.startMinute !== qh.endMinute, {
    message: 'Start and end times must differ',
  });

let formIdCounter = 0;
function nextStepId() {
  formIdCounter += 1;
  return `policy-step-${formIdCounter}`;
}

function policyToForm(policy: ReminderPolicy): FormState {
  return {
    enabled: policy.enabled,
    cadence: policy.cadence.map((step) => {
      const { offsetValue, offsetUnit } = offsetMinutesToFormValue(step.offsetMinutes);
      return {
        id: nextStepId(),
        offsetValue,
        offsetUnit,
        channel: step.channel,
        templateKey: step.templateKey ?? '',
      };
    }),
    maxAttempts: policy.maxAttempts,
    quietHours: policy.quietHours ?? null,
    aiPersonalization: policy.aiPersonalization,
    aiSendTimeOptimization: policy.aiSendTimeOptimization,
    aiChannelSelection: policy.aiChannelSelection,
    aiVoiceFallback: policy.aiVoiceFallback,
    templates: policy.templates ?? [],
  };
}

function validate(state: FormState): { ok: boolean; errors: FormErrors } {
  const errors: FormErrors = { cadence: [] };
  const templatesByChannel = new Map<string, Set<string>>();

  for (const template of state.templates) {
    const keys = templatesByChannel.get(template.channel) ?? new Set<string>();
    keys.add(template.key);
    templatesByChannel.set(template.channel, keys);
  }

  if (state.cadence.length === 0) {
    errors.cadenceRoot = 'Cadence must have at least one step.';
  }

  errors.cadence = state.cadence.map((step) => {
    const result = cadenceStepSchema.safeParse(step);
    if (!result.success) {
      return result.error.issues[0]?.message ?? 'Invalid step';
    }
    const templateKey = step.templateKey?.trim();
    if (templateKey && !templatesByChannel.get(step.channel)?.has(templateKey)) {
      return `Select a ${step.channel} template or use the default fallback.`;
    }
    return undefined;
  });

  if (!Number.isInteger(state.maxAttempts) || state.maxAttempts < 1) {
    errors.maxAttempts = 'Max attempts must be a whole number at least 1';
  }

  if (state.quietHours) {
    const qhResult = quietHoursSchema.safeParse(state.quietHours);
    if (!qhResult.success) {
      errors.quietHours = qhResult.error.issues[0]?.message ?? 'Invalid quiet hours';
    }
  }

  const ok =
    !errors.cadenceRoot &&
    !errors.maxAttempts &&
    !errors.quietHours &&
    errors.cadence.every((error) => !error);

  return { ok, errors };
}

function formToDto(state: FormState): UpdateReminderPolicyDto {
  return {
    enabled: state.enabled,
    cadence: state.cadence.map(formStepToCadence),
    maxAttempts: state.maxAttempts,
    quietHours: state.quietHours ?? null,
    aiPersonalization: state.aiPersonalization,
    aiSendTimeOptimization: state.aiSendTimeOptimization,
    aiChannelSelection: state.aiChannelSelection,
    aiVoiceFallback: state.aiVoiceFallback,
    templates: state.templates,
  };
}

export function ReminderPolicyForm({
  policy,
  formId,
  readOnly = false,
  onSaved,
  onStateChange,
}: ReminderPolicyFormProps) {
  const update = useUpdateReminderPolicy();
  const [state, setState] = useState<FormState>(() => policyToForm(policy));
  const [errors, setErrors] = useState<FormErrors>({ cadence: [] });
  const [openSections, setOpenSections] = useState<Record<SectionKey, boolean>>({
    delivery: true,
    cadence: true,
    quietHours: false,
    ai: false,
    templates: false,
  });
  const initialStateRef = useRef<FormState>(state);

  useEffect(() => {
    const next = policyToForm(policy);
    setState(next);
    setErrors({ cadence: [] });
    initialStateRef.current = next;
  }, [policy]);

  const isDirty = useMemo(
    () => JSON.stringify(state) !== JSON.stringify(initialStateRef.current),
    [state],
  );

  const aiValues = useMemo(
    () => ({
      aiPersonalization: state.aiPersonalization,
      aiSendTimeOptimization: state.aiSendTimeOptimization,
      aiChannelSelection: state.aiChannelSelection,
      aiVoiceFallback: state.aiVoiceFallback,
    }),
    [
      state.aiPersonalization,
      state.aiSendTimeOptimization,
      state.aiChannelSelection,
      state.aiVoiceFallback,
    ],
  );

  const aiEnabledCount = Object.values(aiValues).filter(Boolean).length;

  const onStateChangeRef = useRef(onStateChange);
  onStateChangeRef.current = onStateChange;

  useEffect(() => {
    onStateChangeRef.current?.({
      isDirty,
      isPending: update.isPending,
      cadenceCount: state.cadence.length,
      hasQuietHours: state.quietHours !== null,
      aiCount: aiEnabledCount,
      templateCount: state.templates.length,
    });
  }, [
    isDirty,
    update.isPending,
    state.cadence.length,
    state.quietHours,
    aiEnabledCount,
    state.templates.length,
  ]);

  function toggleSection(section: SectionKey) {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  }

  const [autoExpandStepId, setAutoExpandStepId] = useState<string | null>(null);

  function addCadenceStep(channel: ReminderChannel) {
    const step = makeCadenceStepForChannel(channel);
    setState((prev) => ({ ...prev, cadence: [...prev.cadence, step] }));
    setAutoExpandStepId(step.id);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const { ok, errors: nextErrors } = validate(state);
    setErrors(nextErrors);
    if (!ok) return;
    update.mutate(
      { type: policy.type, dto: formToDto(state) },
      {
        onSuccess: onSaved,
      },
    );
  }

  return (
    <form
      id={formId}
      onSubmit={handleSubmit}
      className="flex min-h-0 flex-1 flex-col overflow-hidden"
    >
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-5">
        <FormAccordionCard
          header={
            <CollapsibleSectionHeader
              icon={Bell}
              label="Delivery"
              open={openSections.delivery}
              onToggle={() => toggleSection('delivery')}
              badge="required"
              statusBadge={state.enabled ? 'Enabled' : 'Disabled'}
            />
          }
          open={openSections.delivery}
        >
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-4 rounded-md border bg-muted/30 p-3">
              <div className="flex items-center gap-1.5">
                <h3 className="text-sm font-medium">Enabled</h3>
                <HintTooltip>When off, no new reminders of this type will be created.</HintTooltip>
              </div>
              <Switch
                checked={state.enabled}
                onCheckedChange={(next) => setState((prev) => ({ ...prev, enabled: next }))}
                disabled={readOnly}
                aria-label="Enabled"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="max-attempts">Max attempts</Label>
                <HintTooltip>
                  Hard cap on how many steps run before the reminder expires.
                </HintTooltip>
              </div>
              <NumberInput
                id="max-attempts"
                min={1}
                step={1}
                className="w-32"
                value={state.maxAttempts}
                emptyValue={0}
                allowDecimal={false}
                onChange={(next) => setState((prev) => ({ ...prev, maxAttempts: next ?? 0 }))}
                disabled={readOnly}
              />
              {errors.maxAttempts ? (
                <p className="text-xs text-destructive">{errors.maxAttempts}</p>
              ) : null}
            </div>
          </div>
        </FormAccordionCard>

        <FormAccordionCard
          header={
            <CollapsibleSectionHeader
              icon={CalendarClock}
              label="Cadence"
              open={openSections.cadence}
              onToggle={() => toggleSection('cadence')}
              badge="required"
              statusBadge={`${state.cadence.length} steps`}
              extra={
                !readOnly && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button type="button" variant="ghost" size="icon" className="h-6 w-6">
                        <Plus className="size-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onSelect={() => addCadenceStep('SMS')}>
                        <MessageSquare className="size-4" />
                        Send SMS
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => addCadenceStep('EMAIL')}>
                        <Mail className="size-4" />
                        Send email
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => addCadenceStep('VOICE')}>
                        <PhoneCall className="size-4" />
                        Voice call
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )
              }
            />
          }
          open={openSections.cadence}
          bodyClassName="p-0"
        >
          <CadenceEditor
            value={state.cadence}
            templates={state.templates}
            onChange={(next) => setState((prev) => ({ ...prev, cadence: next }))}
            errors={errors.cadence}
            rootError={errors.cadenceRoot}
            readOnly={readOnly}
            autoExpandId={autoExpandStepId}
          />
        </FormAccordionCard>

        <FormAccordionCard
          header={
            <CollapsibleSectionHeader
              icon={Clock}
              label="Quiet Hours"
              open={openSections.quietHours}
              onToggle={() => toggleSection('quietHours')}
              badge="optional"
              statusBadge={state.quietHours ? 'Enabled' : undefined}
            />
          }
          open={openSections.quietHours}
        >
          <QuietHoursEditor
            value={state.quietHours}
            onChange={(next) => setState((prev) => ({ ...prev, quietHours: next }))}
            error={errors.quietHours}
          />
        </FormAccordionCard>

        <FormAccordionCard
          header={
            <CollapsibleSectionHeader
              icon={Bot}
              label="AI Features"
              open={openSections.ai}
              onToggle={() => toggleSection('ai')}
              badge="optional"
              statusBadge={`${aiEnabledCount} active`}
            />
          }
          open={openSections.ai}
        >
          <AiTogglesPanel
            values={aiValues}
            onChange={(key, next) => setState((prev) => ({ ...prev, [key]: next }))}
            readOnly={readOnly}
          />
        </FormAccordionCard>

        <FormAccordionCard
          header={
            <CollapsibleSectionHeader
              icon={MessageSquareText}
              label="Templates"
              open={openSections.templates}
              onToggle={() => toggleSection('templates')}
              badge="optional"
              statusBadge={`${state.templates.length} templates`}
            />
          }
          open={openSections.templates}
        >
          <TemplateEditor
            value={state.templates}
            onChange={(next) => setState((prev) => ({ ...prev, templates: next }))}
            readOnly={readOnly}
            reminderType={policy.type}
          />
        </FormAccordionCard>
      </div>
    </form>
  );
}
